import React, { memo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileCopyIcon from '@mui/icons-material/FileCopyOutlined';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tooltip from '@mui/material/Tooltip';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { ConfirmActionDialog } from '../uiComponents';
import colors from './colors';
import { questionTypeMask } from './helper';
import { iconTypes } from './iconTypes';
import { deleteQuestionWarningMessage, hasAssetDataLinks } from './deleteQuestionAlert';
import { InfoQuestion } from './infoQuestion';
import { questionTypesList } from './questionTypes';
import { selectorTypes } from './selectorTypes';

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

const QuestionMenu = memo(
  (props: {
    index: number;
    question: any;
    onAddHandler: any;
    onDeleteHandler: any;
    onDuplicateHandler: any;
    onTypeChange: any;
    disableDelete: boolean;
    addSectionHandler: any;
    availableFileUpload: boolean;
  }) => {
    const {
      index,
      question,
      onAddHandler,
      onDeleteHandler,
      onDuplicateHandler,
      onTypeChange,
      disableDelete,
      addSectionHandler,
      availableFileUpload,
    } = props;

    const [openedAlert, setOpenedAlert] = useState(false);
    const { checkFlag } = useFeatureFlags();

    const lang = useLanguage() as any;

    const deleteHandler = () => {
      setOpenedAlert(false);
      onDeleteHandler(question.id);
    };

    const styles: StyleSheet = {
      iconContainer: {
        cursor: 'pointer',
        paddingTop: '3px',
        marginLeft: '14px',
      },
      icon: {
        fontSize: 18,
      },
    };

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          padding: '0 3px 0',
        }}
      >
        <div style={{ flex: 1 }}>
          <Select
            MenuProps={{ autoFocus: false }}
            disableUnderline
            variant='standard'
            value={questionTypeMask(question.type)}
            onChange={(e: any) => onTypeChange(question.id, e.target.value)}
            renderValue={(typeValue: any) => {
              const value = questionTypesList.includes(typeValue) ? typeValue : 'deprecated';
              const icon = iconTypes(15, 5, colors.primary)[value];
              return (
                <Tooltip title={lang.form_builder.questionType}>
                  <div
                    className={'formBuilderTypeQuestionMenuSelect'}
                    style={{
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {icon}
                    {lang.types[value]}
                  </div>
                </Tooltip>
              );
            }}
          >
            {selectorTypes
              .filter((type) => (availableFileUpload ? true : type.value !== 'attachment'))
              .filter(
                (type) =>
                  type.value !== 'select_activity_period_time' || checkFlag('ActivityPeriodTime'),
              )
              .map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                >
                  <InfoQuestion
                    type={type.value}
                    source={type.pic}
                  >
                    <div
                      style={{
                        fontSize: 12,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      {iconTypes(15, 5, colors.primary)[type.value]}
                      {lang.types[type.value]}
                    </div>
                  </InfoQuestion>
                </MenuItem>
              ))}
          </Select>
        </div>
        <div style={{ display: 'flex', flex: 1 }} />
        <div
          style={styles.iconContainer}
          onClick={() => addSectionHandler(index)}
        >
          <Tooltip title={lang.form_builder.section}>
            <ViewAgendaOutlinedIcon style={styles.icon} />
          </Tooltip>
        </div>
        <div
          style={styles.iconContainer}
          onClick={() => onDuplicateHandler(question.id, index)}
        >
          <Tooltip title={lang.form_builder.copy}>
            <FileCopyIcon style={{ fontSize: 16 }} />
          </Tooltip>
        </div>
        <div
          style={styles.iconContainer}
          onClick={() => {
            if (!disableDelete) setOpenedAlert(true);
          }}
        >
          <Tooltip title={lang.form_builder.delete}>
            <DeleteOutlineIcon
              style={styles.icon}
              color={disableDelete ? 'disabled' : 'inherit'}
            />
          </Tooltip>
        </div>

        <div
          style={{
            cursor: 'pointer',
            marginLeft: '14px',
            backgroundColor: colors.primary,
            borderRadius: '100px',
            height: '18px',
            width: '18px',
          }}
          onClick={() => onAddHandler(index)}
        >
          <Tooltip title={lang.form_builder.add}>
            <AddIcon style={{ ...styles.icon, color: 'white', marginBottom: '9px' }} />
          </Tooltip>
        </div>
        {openedAlert && (
          <ConfirmActionDialog
            isOpen={openedAlert}
            onClose={() => setOpenedAlert(false)}
            warningTitle={lang.form_builder.areYouSure}
            warningMessage={deleteQuestionWarningMessage(hasAssetDataLinks(question), {
              questionAlert: lang.form_builder.questionAlert,
              withAssetLinksPrefix: lang.form_builder.questionAlertWithAssetLinksPrefix,
              withAssetLinksHighlight: lang.form_builder.questionAlertWithAssetLinksHighlight,
            })}
            onConfirmAction={deleteHandler}
            confirmActionButtonText={lang.form_builder.yesDelete}
          />
        )}
      </div>
    );
  },
);

export { QuestionMenu };
